import { ActivatedRoute, Router } from '@angular/router'
import { inject } from '@angular/core'
import { CoreService } from '../../services'
import { TranslateService } from '@ngx-translate/core'
import { StorageKeyService } from 'src/app/services/storage-key.service'
import { Observer } from 'depoto-core/src/utils'
import { User } from 'depoto-core/src/entities'

type StorageKey = { type: string; key: string }
type LoadErrorOptions = {
  forbiddenRedirectRouteTo?: any[]
}

export abstract class BaseDetail {
  protected keyService = inject(StorageKeyService)
  private accountSwitchObserver?: Observer
  private accountSwitchEmail?: string
  protected constructor(
    public route: ActivatedRoute,
    public router: Router,
    public core: CoreService,
    public translateService: TranslateService
  ) {}

  // The currently displayed entity is fetched once (imperatively), not through the query
  // client, so switching the active account (box-user.component's switchTo/onSessionsStorageChange)
  // never re-runs the permission check that guards it — resetQueries() only refetches tanstack
  // query-managed data. Subscribing to currentUserEmitter lets a detail page/service re-fetch and
  // re-validate as soon as the active user actually changes.
  //
  // MultiSessionService.switchToSession() ends up calling depoto-core's afterSetCurrentUser()
  // twice for a single switch (once via OAuthService.getRefreshToken()'s internal onLoginEmitter
  // -> UserService.setCurrentUser() chain, once explicitly from reloadUserProfile()), so
  // currentUserEmitter fires twice per switch. Without deduping, onSwitch would run refreshData()
  // twice concurrently; the second onLoadError() would then compute its redirect from a URL the
  // first call had already navigated away from (e.g. "/orders" -> "/orderss").
  protected watchAccountSwitch(onSwitch: (user: User) => void): void {
    this.unwatchAccountSwitch()
    this.accountSwitchEmail = this.core.services.user.user?.email
    this.accountSwitchObserver = this.core.services.user.currentUserEmitter.subscribe().then(user => {
      if (user?.email === this.accountSwitchEmail) return
      this.accountSwitchEmail = user?.email
      // A full logout (no session left to fall back to) also emits here with an empty user —
      // there's no session left to re-validate the entity against, and the app is already
      // tearing down auth state, so refetching would just fire a doomed request.
      if (!user) return
      onSwitch(user)
    })
  }

  protected unwatchAccountSwitch(): void {
    this.accountSwitchObserver?.unsubscribe()
    this.accountSwitchObserver = undefined
  }

  getStorageKeyForEntity(entity: string): StorageKey {
    return this.keyService.getStorageKeyForEntity(entity, '', false)
  }
  /*
  saveViewSettings(viewName: string, data: any): void {
    this.core.services.storage.set(this.core.services.storage.keys.views[viewName], data)
  }
  loadViewSettings(viewName: string): any {
    return this.core.services.storage.getSync(this.core.services.storage.keys.views[viewName])
  }
   -- never used
  */

  onLoadError(errorCode: number, options: LoadErrorOptions = {}) {
    switch (errorCode) {
      case 404:
      default:
        alert(this.translateService.instant('base-detail.not-found'))
        return this.router.navigate([`/${this.router.url.split('/')[1]}s`])
      case 403:
        return this.router.navigate(options.forbiddenRedirectRouteTo ?? [`/${this.router.url.split('/')[1]}s`], {
          replaceUrl: true,
        })
    }
  }
}
